import {
  OccurrenceStepEnum,
  OccurrenceStepInfoText,
  OccurrenceStepLabelEnum,
} from "../../../enums/occurrenceStep";
import { Occurrence, OccurrenceDispatch, OccurrenceReview, OccurrenceWithTimeSpent } from "../../../models/occurrence";
import { GenericCard, SkeletonGenericCard } from "./genericCards";
import { SkeletonVehicleCard, VehicleCard } from "./vehicleCards";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useRef, useEffect, useState, useCallback } from "react";
import { Step } from "../../../models/step";
import "../styles.scss"
import { OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";

interface ICardViewProps {
  occurrenceList: Occurrence[];
  occurrenceWithTimeSpentList: OccurrenceWithTimeSpent[];
  partners?: { id: string, name: string }[];
  isValidStep: (requiredFields: string[], occurrence: Occurrence) => boolean;
  loading: boolean;
  changeStep: (
    service: Occurrence,
    fromStepId: number,
    to: OccurrenceStepEnum
  ) => Promise<Occurrence | null>;
  save: (occurrence: Occurrence) => Promise<boolean>;
  getNotFilledRequiredFields: (requiredFields: string[], occurrence: Occurrence) => string[];
  finishOccurrence: (occurrence: Occurrence, finalizationReasonType?: string, finalizationReasonDescription?: string, fromStepIp?: number) => Promise<boolean>;
  disabledAllFields?: boolean;
  persona: 'MANAGER' | 'TOWER' | 'CONSULTANT' | 'PARTNERSHIP';
  finalized?: boolean;
  review: OccurrenceReview[];
  reviewStep: (occurrenceId: string, step: OccurrenceStepEnum, rate: number, comment?: string) => Promise<void>;
  getReview: (occurrenceId: string) => Promise<void>;
  occurrenceStepAvailable: string[];
  addDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch) => Promise<{ wasCreated: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  cancelDispatch?: (occurrence: Occurrence, dispatch: OccurrenceDispatch, reason: string, description: string) => Promise<{ wasCanceled: boolean, error?: string, dispatch?: OccurrenceDispatch }>;
  steps?: Step[];
  isSidebarOpen?: boolean;
  onRefresh: () => Promise<void>;
}

const CardView = (props: ICardViewProps) => {
  const {
    occurrenceList,
    occurrenceWithTimeSpentList,
    partners,
    isValidStep,
    loading,
    save,
    changeStep,
    getNotFilledRequiredFields,
    finishOccurrence,
    disabledAllFields,
    persona,
    finalized,
    review,
    reviewStep,
    getReview,
    occurrenceStepAvailable,
    addDispatch,
    cancelDispatch,
    steps,
    isSidebarOpen = false,
    onRefresh,
  } = props;

  const isMobile = useMediaQuery("(max-width: 768px)");
  const smallMobile = useMediaQuery("(max-width: 480px)");

  const getSidebarWidth = useCallback(() => {
    const isFleetManager = window.location.pathname.includes('/fleet-manager');
    const width = !isSidebarOpen ? (smallMobile ? 65 : 80) : (isFleetManager ? 410 : 430);
    return width;
  }, [isSidebarOpen, smallMobile]);

  const fakeScrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const stickyHeaderRef = useRef<HTMLDivElement>(null);
  const stickyFakeScrollRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const mobileHeaderRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasMoved, setHasMoved] = useState(false); 
  const [stickyScrollLeft, setStickyScrollLeft] = useState(0);

  const [showStickyHeader, setShowStickyHeader] = useState(false);

  const kanbanColumnWidth = 320;
  const kanbanColumnGap = 20;
  const kanbanColumnTotalWidth = kanbanColumnWidth + kanbanColumnGap;

  const contentWidth = isMobile
    ? `${(occurrenceStepAvailable.length + 1) * kanbanColumnTotalWidth}px`
    : `${occurrenceStepAvailable.length * kanbanColumnTotalWidth}px`;
  const maxHeight = "100%";
  const scrollbarHeight = 30; 

  const getThresholds = useCallback(() => {
    return {
      show: smallMobile ? 50 : isMobile ? 70 : 10,
      hide: smallMobile ? 30 : isMobile ? 50 : 80,
    };
  }, [smallMobile, isMobile]);


  useEffect(() => {
    const handleFakeScroll = () => {
      if (contentRef.current && fakeScrollRef.current) {
        contentRef.current.scrollLeft = fakeScrollRef.current.scrollLeft;
        stickyFakeScrollRef.current &&
          (stickyFakeScrollRef.current.scrollLeft =
            fakeScrollRef.current.scrollLeft);
        setStickyScrollLeft(fakeScrollRef.current.scrollLeft);
      }
    };

    const handleContentScroll = () => {
      if (contentRef.current && fakeScrollRef.current) {
        fakeScrollRef.current.scrollLeft = contentRef.current.scrollLeft;
        stickyFakeScrollRef.current &&
          (stickyFakeScrollRef.current.scrollLeft = contentRef.current.scrollLeft);
        setStickyScrollLeft(contentRef.current.scrollLeft);
      }
    };

    const handleStickyScroll = () => {
      // Removido porque o sticky header não deve mais ter scroll próprio
    };

    const handleStickyFakeScroll = () => {
      if (stickyFakeScrollRef.current && contentRef.current && fakeScrollRef.current) {
        const scroll = stickyFakeScrollRef.current.scrollLeft;
        contentRef.current.scrollLeft = scroll;
        fakeScrollRef.current.scrollLeft = scroll;
        setStickyScrollLeft(scroll);
      }
    };

    const connectListeners = () => {
      fakeScrollRef.current?.removeEventListener("scroll", handleFakeScroll);
      contentRef.current?.removeEventListener("scroll", handleContentScroll);
      stickyFakeScrollRef.current?.removeEventListener("scroll", handleStickyFakeScroll);

      fakeScrollRef.current?.addEventListener("scroll", handleFakeScroll);
      contentRef.current?.addEventListener("scroll", handleContentScroll);
      stickyFakeScrollRef.current?.addEventListener("scroll", handleStickyFakeScroll);

      if (contentRef.current) {
        const currentScrollLeft = contentRef.current.scrollLeft;
        stickyFakeScrollRef.current &&
          (stickyFakeScrollRef.current.scrollLeft = currentScrollLeft);
        setStickyScrollLeft(currentScrollLeft);
      }
    };

    connectListeners();

    const rafId = requestAnimationFrame(() => {
      connectListeners();
      if (showStickyHeader && stickyFakeScrollRef.current && contentRef.current) {
        const currentScrollLeft = contentRef.current.scrollLeft;
        stickyFakeScrollRef.current.scrollLeft = currentScrollLeft;
        setStickyScrollLeft(currentScrollLeft);
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      fakeScrollRef.current?.removeEventListener("scroll", handleFakeScroll);
      contentRef.current?.removeEventListener("scroll", handleContentScroll);
      stickyFakeScrollRef.current?.removeEventListener("scroll", handleStickyFakeScroll);
    };
  }, [showStickyHeader]);


  useEffect(() => {
    const checkHeaderPosition = () => {
      const targetRef = !isMobile
        ? headerRef.current
        : mobileHeaderRef.current || contentRef.current;
      if (!targetRef) return;

      const rect = targetRef.getBoundingClientRect();
      const { show, hide } = getThresholds();
      setShowStickyHeader((prev) => {
        if (!prev && rect.bottom <= show) {

          return true;
        } else if (prev && rect.bottom >= hide) {

          return false;
        }
        return prev;
      });
    };

    const handleScroll = () => {

      if (showStickyHeader) {
        const targetRef = !isMobile
          ? headerRef.current
          : mobileHeaderRef.current || contentRef.current;
        if (targetRef) {
          const rect = targetRef.getBoundingClientRect();
          const { hide } = getThresholds();
          if (rect.bottom >= hide) {

            setShowStickyHeader(false);
            return;
          }
        }
      }
      checkHeaderPosition();
    };

    const findScrollableParent = (
      element: HTMLElement | null
    ): HTMLElement | null => {
      if (!element) return null;
      const style = window.getComputedStyle(element);
      const hasScroll =
        style.overflow === "auto" ||
        style.overflow === "scroll" ||
        style.overflowY === "auto" ||
        style.overflowY === "scroll";

      if (hasScroll && element.scrollHeight > element.clientHeight) {

        return element;
      }
      return findScrollableParent(element.parentElement);
    };

    const scrollableContainer = findScrollableParent(
      headerRef.current?.parentElement || null
    );

    const elements: (Window | Document | HTMLElement)[] = [window, document];
    if (scrollableContainer) {
      elements.push(scrollableContainer as HTMLElement);

    }

    const commonScrollables = document.querySelectorAll(
      ".main-content, .page-content, .layout-content, [data-scroll='true']"
    );
    commonScrollables.forEach((el) => {

      el.addEventListener("scroll", handleScroll, { passive: true });
    });

    elements.forEach((element) => {
      element.addEventListener("scroll", handleScroll, { passive: true });

      if (smallMobile || isMobile) {
        element.addEventListener("touchmove", handleScroll, { passive: true });
        element.addEventListener("touchstart", handleScroll, { passive: true });
        element.addEventListener("wheel", handleScroll, { passive: true });
      }
    });
    checkHeaderPosition();
    const interval = setInterval(() => {
      checkHeaderPosition();
    }, 2000);

    return () => {
      clearInterval(interval);
      elements.forEach((element) => {
        element.removeEventListener("scroll", handleScroll);
        if (smallMobile || isMobile) {
          element.removeEventListener("touchmove", handleScroll);
          element.removeEventListener("touchstart", handleScroll);
          element.removeEventListener("wheel", handleScroll);
        }
      });
      commonScrollables.forEach((el) => {
        el.removeEventListener("scroll", handleScroll);
      });
    };
  }, [showStickyHeader, isMobile, smallMobile, getThresholds]);


  const startDrag = (e: React.MouseEvent) => {
    setIsDragging(true);
    setHasMoved(false); 
    setStartX(e.pageX - (contentRef.current?.offsetLeft || 0));
    setScrollLeft(contentRef.current?.scrollLeft || 0);
  };

  const onDrag = (e: React.MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - (contentRef.current?.offsetLeft || 0);
    const walk = (x - startX) * -1;
    if (Math.abs(walk) > 5) {
      setHasMoved(true);
    }
    if (contentRef.current) contentRef.current.scrollLeft = scrollLeft + walk;
  };
  
  const stopDrag = (e: React.MouseEvent) => {
    if (hasMoved) {
      e.preventDefault();
      e.stopPropagation();
      const preventClicks = (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
      };
      document.addEventListener('click', preventClicks, { capture: true });
      setTimeout(() => {
        document.removeEventListener('click', preventClicks, { capture: true });
      }, 100);
    }
    setIsDragging(false);
    setHasMoved(false);
  };

  return (
    <div className="bg-white p-[12px] pt-[6px] kanban-cards">

      {showStickyHeader && (
        <div
          className="fixed shadow-md bg-white kanban-cards sticky-header"
          style={{
            top: '50px',
            left: `${getSidebarWidth()}px`,
            right: smallMobile ? '15px' : '30px',
            paddingLeft: '12px',
            paddingRight: '12px',
            transition: 'opacity 0.1s ease-out',
            opacity: 1,
            zIndex: 5,
          }}
        >
          <div className="flex">
            {!isMobile && (
              <div
                className="kanban-cards__vehicles"
                style={{
                  minWidth: `${kanbanColumnTotalWidth}px`,
                  backgroundColor: "#ffffff",
                  paddingBottom: 0
                }}
              >
                <div style={{ padding: "8px", backgroundColor: "#C7D1D9" }}>
                  <span className="text-size-16 font-semibold flex items-center bg-white uppercase card-title">
                    Veículo
                  </span>
                </div>
              </div>
            )}
            <div className="kanban-cards__generics relative w-full select-none">
              <div 
                ref={stickyFakeScrollRef}
                id="custom-sticky-fake-scroll"
                className={`w-full overflow-x-auto ${isMobile ? 'h-[20px]' : 'h-[31px]'}`}
                style={{ scrollbarWidth: 'auto' }}
              >
                <div style={{ width: contentWidth, height: scrollbarHeight }} />
              </div>
              
              <div
                ref={stickyHeaderRef}
                onMouseDown={startDrag}
                onMouseMove={onDrag}
                onMouseUp={stopDrag}
                onMouseLeave={stopDrag}
                className={`${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
                style={{ cursor: isDragging ? "grabbing" : "grab", overflowX: 'hidden' }}
              >
                <div 
                  className={`flex bg-[#C7D1D9] pl-[8px] pr-[8px] pb-[8px] pt-[8px]`} 
                  style={{ 
                    width: contentWidth,
                    transform: `translateX(-${stickyScrollLeft}px)`,
                    transition: isDragging ? 'none' : ''
                  }}
                >
                  {(isMobile ? ["Veículo"] : []).concat(occurrenceStepAvailable.map(step => OccurrenceStepLabelEnum[step as keyof typeof OccurrenceStepLabelEnum]))
                    .map(step => (
                      <div key={step}
                        className="width-320"
                        style={{ minWidth: `${kanbanColumnWidth}px`, marginRight: `${kanbanColumnGap}px` }}>
                        <div 
                        style={{ backgroundColor: "#C7D1D9" }}
                        >
                          <span className={`text-size-16 font-semibold flex items-center bg-white uppercase card-title`}>
                            {step}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="flex"
        style={{
          paddingTop: showStickyHeader ? '80px' : '0',
          transition: 'padding-top 0.1s ease-out'
        }}
      >
        {!isMobile && (
          <div className="gap-4 kanban-cards__vehicles">
            <div ref={headerRef} style={{ backgroundColor: "#C7D1D9", padding: "8px 8px 0px 8px", width: `${kanbanColumnTotalWidth}px`, minWidth: `${kanbanColumnTotalWidth}px` }} className="width-320">
              <span className="text-size-16 font-semibold flex items-center bg-white uppercase card-title">
                Veículo
              </span>
            </div>

            {loading &&
              new Array(5).fill(0).map((_, i) => (
                <div key={i} style={{ height: "307px" }}>
                  <SkeletonVehicleCard index={i} />
                </div>
              ))}

            {occurrenceList.map((occurrence) => (
              <div key={occurrence.id} style={{ height: "307px" }}>
                <VehicleCard
                  index={occurrenceList.indexOf(occurrence)}
                  occurrence={occurrence}
                  occurrenceWithTimeSpent={occurrenceWithTimeSpentList.find(el => el.occurrenceId === occurrence.id)}
                  isValidStep={(fields) => isValidStep(fields, occurrence)}
                  loading={loading}
                  save={() => save(occurrence)}
                  disabledAllFields={disabledAllFields}
                  persona={persona}
                  finalized={finalized || occurrence.finished}
                  occurrenceStepAvailable={occurrenceStepAvailable}
                  partners={partners}
                  changeStep={changeStep}
                  getNotFilledRequiredFields={getNotFilledRequiredFields}
                />
              </div>
            ))}
          </div>
        )}

        <div className={`bg-white kanban-cards__generics ${isMobile ? "!pl-0" : ""}`}>
          <div className="relative w-full select-none">
            <div 
              ref={fakeScrollRef}  
              id="custom-fake-scroll"
              className={`w-full overflow-x-auto ${isMobile ? 'h-[20px]' : 'h-[25px]'}`}
              style={{ scrollbarWidth: 'auto' }}
            >
              <div style={{ width: contentWidth, height: scrollbarHeight }} />
            </div>
            

            <div
              ref={contentRef}
              onMouseDown={startDrag}
              onMouseMove={onDrag}
              onMouseUp={stopDrag}
              onMouseLeave={stopDrag}
              className={`overflow-x-auto ${isDragging ? "cursor-grabbing" : "cursor-grab"} hide-scrollbar`}
              style={{ maxHeight }}
            >
              <div style={{ width: contentWidth }}>
                <div>
                  <div className="flex bg-[#C7D1D9] pl-[8px] pr-[8px] pb-[0px] pt-[8px] mt-[6px]">
                    {(isMobile ? ["Veículo"] : []).concat(occurrenceStepAvailable.map(step => OccurrenceStepLabelEnum[step as keyof typeof OccurrenceStepLabelEnum]))
                      .map((label, idx) => {
                        const stepKey = isMobile && idx === 0 ? null : occurrenceStepAvailable[isMobile ? idx - 1 : idx];
                        const stepEnum = stepKey ? OccurrenceStepEnum[stepKey as keyof typeof OccurrenceStepEnum] : null;
                        const infoText = stepEnum ? (OccurrenceStepInfoText[stepEnum as keyof typeof OccurrenceStepInfoText] || "") : "";
                        return (
                          <div key={label} className="width-320" style={{ minWidth: `${kanbanColumnWidth}px`, marginRight: `${kanbanColumnGap}px` }}>
                            <div style={{ backgroundColor: "#C7D1D9" }}>
                              <span className={`text-size-16 font-semibold flex items-center bg-white uppercase card-title justify-between`}>
                                {label}
                                {stepKey && infoText && (
                                  <OverlayTrigger
                                    key={stepKey}
                                    trigger="click"
                                    placement="bottom"
                                    overlay={
                                      <Tooltip id={`tooltip-${stepKey}`} allowOnTouch>
                                        <div className="tooltip-card flex flex-column padding-10 gap-6 max-w-[200px]">
                                          <h3 className="text-size-14 font-bold">{label}</h3>
                                          <span className="text-size-12 break-words" >{infoText}</span>
                                        </div>
                                      </Tooltip>
                                    }
                                  >
                                    <span className="cursor-pointer rioglyph rioglyph-info-sign icon-revision ml-2 " style={{ fontSize: "24px" }} />
                                  </OverlayTrigger>
                                )}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>

                  {loading && new Array(5).fill(0).map((_, i) => (
                    <div className="flex" key={i} style={{ height: "307px" }}>
                      {new Array(occurrenceStepAvailable.length).fill(0).map((_, j) => (
                        <SkeletonGenericCard key={j} index={j} />
                      ))}
                    </div>
                  ))}


                  {occurrenceList.map((occurrence, index) => (
                    <div className="flex" key={occurrence.id} style={{ height: "307px" }}>
                      {isMobile && (
                        <div
                          ref={index === 0 ? mobileHeaderRef : null}
                          className="kanban-cards__vehicles relative pt-0 pr-[20px] !h-[225px] !pb-[30px]"
                          style={{ backgroundColor: "#C7D1D9" }}
                        >
                          <VehicleCard
                            key={occurrence.id}
                            index={index}
                            occurrence={occurrence}
                            occurrenceWithTimeSpent={occurrenceWithTimeSpentList.find(el => el.occurrenceId === occurrence.id)}
                            isValidStep={(fields) => isValidStep(fields, occurrence)}
                            loading={loading}
                            save={() => save(occurrence)}
                            disabledAllFields={disabledAllFields}
                            persona={persona}
                            finalized={finalized || occurrence.finished}
                            occurrenceStepAvailable={occurrenceStepAvailable}
                            partners={partners}
                            changeStep={changeStep}
                            getNotFilledRequiredFields={getNotFilledRequiredFields}
                          />
                        </div>
                      )}

                      {occurrenceStepAvailable.map((stepKey) => {
                        const step = OccurrenceStepEnum[stepKey as keyof typeof OccurrenceStepEnum];
                        const summaryStep = occurrence.steps?.find(s => s.step === step);

                        return (
                          <GenericCard
                            key={`${occurrence.id}-${stepKey}`}
                            occurrence={occurrence}
                            summaryStep={summaryStep}
                            step={step}
                            partners={partners}
                            isValidStep={isValidStep}
                            loading={loading}
                            save={(occurrenceUpdated: Occurrence) => save(occurrenceUpdated)}
                            changeStep={changeStep}
                            getNotFilledRequiredFields={getNotFilledRequiredFields}
                            finishOccurrence={finishOccurrence}
                            disabledAllFields={disabledAllFields}
                            persona={persona}
                            finalized={finalized || occurrence.finished}
                            review={review}
                            reviewStep={reviewStep}
                            getReview={getReview}
                            occurrenceStepAvailable={occurrenceStepAvailable}
                            addDispatch={addDispatch}
                            cancelDispatch={cancelDispatch}
                            steps={steps}
                            onRefresh={onRefresh}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CardView;
